import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, Tabs, Tab, Table, TableHead, TableRow, TableCell,
  TableBody, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Chip, IconButton,
  Autocomplete, Stack, Tooltip, Switch, FormControlLabel, Divider, CircularProgress,
  Snackbar, InputAdornment, Paper,
} from '@mui/material';
import {
  Add, Delete, Send, Visibility, VisibilityOff, Shuffle, ContentCopy, UploadFile, CalendarMonth,
  Check, Edit, Assignment, Groups, Replay,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { apiService, errorMessage } from '../services/api';
import { Group, Participant, MatchPreview, ReminderSettings } from '../types';
import { validateMinParticipants, isValidEmail } from '../utils/validators';
import { formatRelative } from '../utils/formatters';

const TabPanel: React.FC<{ value: number; index: number; children: React.ReactNode }> = ({ value, index, children }) =>
  value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;

const DEFAULT_REMINDERS: ReminderSettings = {
  matchReady: true, addIdeasAfterDays: 7, weeklyNewIdeas: false,
};

type ImportRow = { name: string; email: string; household?: string; exclusions?: string[]; problem?: string };

const parseImportText = (text: string): ImportRow[] => {
  const trimmed = text.trim();
  if (!trimmed) return [];
  try {
    const json = JSON.parse(trimmed);
    if (Array.isArray(json)) {
      return json.map((r: any) => ({
        name: String(r.name || '').trim(), email: String(r.email || '').trim().toLowerCase(),
        household: r.household ? String(r.household).trim() : undefined,
        exclusions: Array.isArray(r.exclusions) ? r.exclusions : undefined,
      }));
    }
  } catch { /* not JSON */ }
  const lines = trimmed.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const first = lines[0].toLowerCase();
  const hasHeader = first.includes('name') && first.includes('email');
  const body = hasHeader ? lines.slice(1) : lines;
  return body.map((line) => {
    const parts = line.split(',').map((p) => p.trim().replace(/^"|"$/g, ''));
    return {
      name: parts[0] || '', email: (parts[1] || '').toLowerCase(),
      household: parts[2] || undefined,
      exclusions: parts[3] ? parts[3].split('|').map((e) => e.trim()).filter(Boolean) : undefined,
    };
  });
};

const GroupManager: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();

  const [group, setGroup] = useState<Group | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  // Add participant
  const [addOpen, setAddOpen] = useState(false);
  const [newP, setNewP] = useState({ name: '', email: '', householdId: '' });
  // Inline edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState({ name: '', email: '' });
  // Draw
  const [preview, setPreview] = useState<MatchPreview[] | null>(null);
  const [drawing, setDrawing] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [drawResult, setDrawResult] = useState<{ notified?: number; failed?: string[] } | null>(null);
  // Import
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  // Settings
  const [settingsForm, setSettingsForm] = useState<any>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!groupId) return;
    try {
      const [g, ps] = await Promise.all([apiService.getGroup(groupId), apiService.getParticipants(groupId)]);
      setGroup(g);
      setParticipants(ps.sort((a, b) => a.name.localeCompare(b.name)));
      setSettingsForm({
        name: g.name,
        budget: g.budget ?? '',
        exchangeDate: g.exchangeDate || '',
        exchangeLocation: g.exchangeLocation || '',
        coOrganizerEmails: g.coOrganizerEmails || [],
        autoExcludePreviousYears: g.settings?.autoExcludePreviousYears ?? true,
        surveyEnabled: g.settings?.surveyEnabled ?? true,
        reminders: { ...DEFAULT_REMINDERS, ...(g.settings?.reminders || {}) },
      });
    } catch (e: any) {
      setError(e?.response?.status === 403 ? 'Only organizers can manage this group.' : errorMessage(e, 'Failed to load group'));
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  const byId = useMemo(() => Object.fromEntries(participants.map((p) => [p.id, p])), [participants]);
  const households = useMemo(
    () => Array.from(new Set(participants.map((p) => p.householdId).filter(Boolean))) as string[],
    [participants]
  );
  const matched = group?.status === 'matched' || group?.status === 'completed';

  // ---------------------------------------------------------------- participants
  const addParticipant = async () => {
    if (!groupId) return;
    if (!newP.name.trim() || !isValidEmail(newP.email)) { setError('Enter a name and a valid email'); return; }
    try {
      const p = await apiService.addParticipant(groupId, {
        name: newP.name.trim(), email: newP.email.trim(), householdId: newP.householdId.trim() || null,
      });
      setParticipants([...participants, p].sort((a, b) => a.name.localeCompare(b.name)));
      setNewP({ name: '', email: '', householdId: '' });
      setAddOpen(false);
      setToast(`${p.name} added`);
    } catch (e) {
      setError(errorMessage(e, 'Failed to add participant'));
    }
  };

  const updateParticipant = async (id: string, data: Partial<Participant>) => {
    if (!groupId) return;
    setBusyId(id);
    try {
      const updated = await apiService.updateParticipant(groupId, id, data);
      setParticipants(participants.map((p) => (p.id === id ? updated : p)));
    } catch (e) {
      setError(errorMessage(e, 'Failed to update participant'));
    } finally {
      setBusyId(null);
    }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    if (!editValues.name.trim() || !isValidEmail(editValues.email)) { setError('Enter a name and a valid email'); return; }
    await updateParticipant(editingId, { name: editValues.name.trim(), email: editValues.email.trim() });
    setEditingId(null);
  };

  const removeParticipant = async (p: Participant) => {
    if (!groupId || !window.confirm(`Remove ${p.name} from the group?${matched ? ' Matches already exist; you will need to redraw.' : ''}`)) return;
    try {
      await apiService.deleteParticipant(groupId, p.id);
      setParticipants(participants.filter((x) => x.id !== p.id));
    } catch (e) {
      setError(errorMessage(e, 'Failed to remove participant'));
    }
  };

  const resend = async (p: Participant) => {
    if (!groupId) return;
    setBusyId(p.id);
    try {
      await apiService.resendMatch(groupId, p.id);
      setToast(`Sent to ${p.name}`);
      await load();
    } catch (e) {
      setError(errorMessage(e, 'Failed to resend'));
    } finally {
      setBusyId(null);
    }
  };

  const copyInvite = async () => {
    const url = `${window.location.origin}/g/${groupId}`;
    try {
      await navigator.clipboard.writeText(url);
      setToast('Link copied. Participants sign in with the email you added them with.');
    } catch {
      window.prompt('Copy this link:', url);
    }
  };

  // ------------------------------------------------------------------------ draw
  const draw = async (dryRun: boolean, confirmRegenerate = false) => {
    if (!groupId) return;
    const minError = validateMinParticipants(participants.length);
    if (minError) { setError(minError); return; }
    setDrawing(true);
    setError(null);
    try {
      const result = await apiService.generateMatches(groupId, { dryRun, confirmRegenerate });
      if (dryRun) {
        setPreview(result.matches || []);
      } else {
        setPreview(null);
        setDrawResult({ notified: result.notified, failed: result.failed });
        setConfirmRegen(false);
        setToast(`Matches drawn. ${result.notified ?? 0} of ${result.matchCount ?? 0} notified.`);
        await load();
      }
    } catch (e: any) {
      if (e?.response?.status === 409 && e.response.data?.code === 'confirm_regenerate') {
        setConfirmRegen(true);
      } else {
        setError(errorMessage(e, 'Failed to generate matches'));
      }
    } finally {
      setDrawing(false);
    }
  };

  const nextYear = async () => {
    if (!groupId || !group) return;
    if (!window.confirm(`Create "${group.name}" for ${group.year + 1} with the same people? Last year's matches will be excluded automatically.`)) return;
    try {
      const g = await apiService.createNextYear(groupId);
      navigate(`/group/${g.id}`);
      setLoading(true);
      setTimeout(load, 0);
    } catch (e) {
      setError(errorMessage(e, 'Failed to create next year\'s group'));
    }
  };

  // ---------------------------------------------------------------------- import
  const importRows = useMemo(() => {
    const rows = parseImportText(importText);
    const seen = new Set(participants.map((p) => p.email));
    return rows.map((r) => {
      let problem: string | undefined;
      if (!r.name || !r.email) problem = 'missing name or email';
      else if (!isValidEmail(r.email)) problem = 'invalid email';
      else if (seen.has(r.email)) problem = 'already in group';
      seen.add(r.email);
      return { ...r, problem };
    });
  }, [importText, participants]);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    file.text().then(setImportText);
    e.target.value = '';
  };

  const doImport = async () => {
    if (!groupId) return;
    const good = importRows.filter((r) => !r.problem);
    if (good.length === 0) { setError('Nothing valid to import'); return; }
    setImporting(true);
    try {
      const result = await apiService.importParticipants(groupId, good);
      setToast(`Imported ${result.created.length}${result.skipped.length ? `, skipped ${result.skipped.length}` : ''}`);
      setImportText('');
      await load();
      setTab(0);
    } catch (e) {
      setError(errorMessage(e, 'Import failed'));
    } finally {
      setImporting(false);
    }
  };

  // -------------------------------------------------------------------- settings
  const saveSettings = async () => {
    if (!groupId || !settingsForm) return;
    setSavingSettings(true);
    try {
      const g = await apiService.updateGroup(groupId, {
        name: settingsForm.name,
        budget: settingsForm.budget === '' ? null : Number(settingsForm.budget),
        exchangeDate: settingsForm.exchangeDate || null,
        exchangeLocation: settingsForm.exchangeLocation || null,
        coOrganizerEmails: settingsForm.coOrganizerEmails,
        settings: {
          autoExcludePreviousYears: settingsForm.autoExcludePreviousYears,
          surveyEnabled: settingsForm.surveyEnabled,
          reminders: settingsForm.reminders,
        },
      } as any);
      setGroup(g);
      setToast('Settings saved');
    } catch (e) {
      setError(errorMessage(e, 'Failed to save settings'));
    } finally {
      setSavingSettings(false);
    }
  };

  const setStatus = async (status: Group['status']) => {
    if (!groupId) return;
    try {
      setGroup(await apiService.updateGroup(groupId, { status }));
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const deleteGroup = async () => {
    if (!groupId) return;
    setDeleting(true);
    try {
      await apiService.deleteGroup(groupId);
      navigate('/dashboard');
    } catch (e) {
      setError(errorMessage(e, 'Failed to delete group'));
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const surveyDone = participants.filter((p) => p.surveyCompleted).length;
  const viewed = participants.filter((p) => p.matchViewedAt).length;

  return (
    <Layout
      title={group ? `${group.name} · ${group.year}` : 'Manage group'}
      backTo="/dashboard"
      loading={loading}
      actions={group ? (
        <Tooltip title="View as participant">
          <IconButton color="inherit" onClick={() => navigate(`/g/${group.id}`)} aria-label="View as participant">
            <Visibility />
          </IconButton>
        </Tooltip>
      ) : undefined}
    >
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {group && settingsForm && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip
                label={group.status === 'matched' ? 'Matched' : group.status === 'completed' ? 'Completed' : 'Setting up'}
                color={group.status === 'matched' ? 'success' : group.status === 'completed' ? 'default' : 'warning'}
                size="small"
              />
              <Chip size="small" variant="outlined" icon={<Groups />} label={`${participants.length} people`} />
              {group.settings?.surveyEnabled !== false && <Chip size="small" variant="outlined" icon={<Assignment />} label={`${surveyDone} surveys`} />}
              {matched && <Chip size="small" variant="outlined" icon={<Visibility />} label={`${viewed} viewed match`} />}
              {group.previousGroupId && <Chip size="small" variant="outlined" label="Excludes last year's pairs" />}
            </Box>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
              <Tab label="Participants" />
              <Tab label="Draw" />
              <Tab label="Import" />
              <Tab label="Settings" />
            </Tabs>

            {/* ------------------------------------------------------ Participants */}
            <TabPanel value={tab} index={0}>
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Button variant="contained" startIcon={<Add />} onClick={() => setAddOpen(true)}>Add person</Button>
                <Button variant="outlined" startIcon={<ContentCopy />} onClick={copyInvite}>Copy group link</Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                People in the same <strong>household</strong> never draw each other. Use <strong>can't draw</strong> for
                anything else (exes, last year's pair from another group, etc.).
              </Typography>

              {participants.length === 0 ? (
                <Alert severity="info">Add at least 3 people, or use the Import tab to paste a list.</Alert>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name / email</TableCell>
                        <TableCell>Household</TableCell>
                        <TableCell>Can't draw</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {participants.map((p) => (
                        <TableRow key={p.id} hover sx={{ opacity: busyId === p.id ? 0.6 : 1 }}>
                          <TableCell sx={{ minWidth: 200 }}>
                            {editingId === p.id ? (
                              <Stack spacing={1}>
                                <TextField size="small" label="Name" value={editValues.name} onChange={(e) => setEditValues({ ...editValues, name: e.target.value })} autoFocus />
                                <TextField size="small" label="Email" value={editValues.email} onChange={(e) => setEditValues({ ...editValues, email: e.target.value })} />
                                <Stack direction="row" spacing={1}>
                                  <Button size="small" variant="contained" startIcon={<Check />} onClick={saveEdit}>Save</Button>
                                  <Button size="small" onClick={() => setEditingId(null)}>Cancel</Button>
                                </Stack>
                              </Stack>
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box>
                                  <Typography sx={{ fontWeight: 600 }}>{p.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">{p.email}</Typography>
                                </Box>
                                <Tooltip title="Edit name or email">
                                  <IconButton size="small" onClick={() => { setEditingId(p.id); setEditValues({ name: p.name, email: p.email }); }}>
                                    <Edit fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ minWidth: 150 }}>
                            <Autocomplete
                              freeSolo size="small" options={households}
                              value={p.householdId || ''}
                              onChange={(_, v) => updateParticipant(p.id, { householdId: (v as string) || null })}
                              onBlur={(e: any) => {
                                const v = (e.target.value || '').trim();
                                if (v !== (p.householdId || '')) updateParticipant(p.id, { householdId: v || null });
                              }}
                              renderInput={(params) => <TextField {...params} placeholder="e.g. Smiths" variant="standard" />}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 200 }}>
                            <Autocomplete
                              multiple size="small" disableCloseOnSelect
                              options={participants.filter((o) => o.id !== p.id)}
                              getOptionLabel={(o) => o.name}
                              value={(p.exclusions || []).map((id) => byId[id]).filter(Boolean)}
                              onChange={(_, v) => updateParticipant(p.id, { exclusions: v.map((o) => o.id) })}
                              renderTags={(value, getTagProps) => value.map((o, i) => <Chip size="small" label={o.name} {...getTagProps({ index: i })} />)}
                              renderInput={(params) => <TextField {...params} variant="standard" placeholder={p.exclusions?.length ? '' : 'Anyone'} />}
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                              {group.settings?.surveyEnabled !== false && (
                                <Tooltip title={p.surveyCompleted ? 'Survey completed' : 'Survey not filled out'}>
                                  <Chip size="small" icon={<Assignment />} label="Survey" color={p.surveyCompleted ? 'success' : 'default'} variant={p.surveyCompleted ? 'filled' : 'outlined'} />
                                </Tooltip>
                              )}
                              {matched && (
                                <Tooltip title={p.matchViewedAt ? `Viewed ${formatRelative(p.matchViewedAt)}` : p.matchNotifiedAt ? `Notified ${formatRelative(p.matchNotifiedAt)}, not viewed yet` : 'Not notified yet'}>
                                  <Chip
                                    size="small"
                                    icon={p.matchViewedAt ? <Visibility /> : <VisibilityOff />}
                                    label={p.matchViewedAt ? 'Viewed' : p.matchNotifiedAt ? 'Sent' : 'Pending'}
                                    color={p.matchViewedAt ? 'success' : 'default'}
                                    variant={p.matchViewedAt ? 'filled' : 'outlined'}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {matched && p.matchedTo && (
                              <Tooltip title="Resend match email/text">
                                <IconButton size="small" onClick={() => resend(p)} disabled={busyId === p.id}><Send fontSize="small" /></IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Remove">
                              <IconButton size="small" color="error" onClick={() => removeParticipant(p)}><Delete fontSize="small" /></IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </TabPanel>

            {/* -------------------------------------------------------------- Draw */}
            <TabPanel value={tab} index={1}>
              {matched ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Matches were drawn{group.matchedAt ? ` ${formatRelative(group.matchedAt)}` : ''}. {viewed} of {participants.length} have looked at theirs.
                  Participant names are not shown here so the draw stays secret even from you.
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Preview first to check the rules work, then draw for real. Everyone gets an email
                  (and a text, if they opted in) with a link to see their person.
                </Alert>
              )}
              {drawResult && (
                <Alert severity={drawResult.failed?.length ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => setDrawResult(null)}>
                  Notified {drawResult.notified}.{drawResult.failed?.length ? ` Could not reach: ${drawResult.failed.join(', ')}. Use Resend from the Participants tab.` : ''}
                </Alert>
              )}
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Button variant="outlined" startIcon={<Shuffle />} onClick={() => draw(true)} disabled={drawing || participants.length < 3}>
                  Preview a draw
                </Button>
                <Button
                  variant="contained" color="secondary" startIcon={matched ? <Replay /> : <Send />}
                  onClick={() => draw(false)} disabled={drawing || participants.length < 3}
                >
                  {drawing ? <CircularProgress size={22} /> : matched ? 'Redraw and notify everyone' : 'Draw and notify everyone'}
                </Button>
                {matched && group.status !== 'completed' && (
                  <Button variant="text" onClick={() => setStatus('completed')}>Mark completed</Button>
                )}
                {matched && (
                  <Button variant="text" startIcon={<CalendarMonth />} onClick={nextYear}>Create {group.year + 1} group</Button>
                )}
              </Stack>
              {participants.length < 3 && <Typography variant="body2" color="text.secondary">Add at least 3 people first.</Typography>}

              {preview && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Preview (not saved, nobody notified)</Typography>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Giver</TableCell><TableCell>→</TableCell><TableCell>Receives</TableCell></TableRow></TableHead>
                    <TableBody>
                      {preview.map((m) => (
                        <TableRow key={m.giverId}><TableCell>{m.giver}</TableCell><TableCell>→</TableCell><TableCell>{m.receiver}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Typography variant="caption" color="text.secondary">Each preview is a fresh random draw; the real draw will differ.</Typography>
                </Paper>
              )}
            </TabPanel>

            {/* ------------------------------------------------------------ Import */}
            <TabPanel value={tab} index={2}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Paste one person per line as <code>Name, email, household, can't-draw-names</code> (household and
                can't-draw are optional; separate several names with <code>|</code>). A header row is fine. JSON works too.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                <Button component="label" variant="outlined" startIcon={<UploadFile />}>
                  Choose CSV file
                  <input hidden type="file" accept=".csv,.txt,.json" onChange={onFile} />
                </Button>
              </Stack>
              <TextField
                fullWidth multiline minRows={5} value={importText} onChange={(e) => setImportText(e.target.value)}
                placeholder={'Jane Smith, jane@example.com, Smiths\nJohn Smith, john@example.com, Smiths\nAunt May, may@example.com'}
                sx={{ mb: 2, fontFamily: 'monospace' }}
              />
              {importRows.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Preview: {importRows.filter((r) => !r.problem).length} to import
                    {importRows.some((r) => r.problem) ? `, ${importRows.filter((r) => r.problem).length} skipped` : ''}
                  </Typography>
                  <Box sx={{ overflowX: 'auto', mb: 2 }}>
                    <Table size="small">
                      <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Household</TableCell><TableCell>Can't draw</TableCell><TableCell /></TableRow></TableHead>
                      <TableBody>
                        {importRows.map((r, i) => (
                          <TableRow key={i} sx={{ opacity: r.problem ? 0.5 : 1 }}>
                            <TableCell>{r.name}</TableCell><TableCell>{r.email}</TableCell><TableCell>{r.household}</TableCell>
                            <TableCell>{r.exclusions?.join(', ')}</TableCell>
                            <TableCell>{r.problem && <Chip size="small" color="warning" label={r.problem} />}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                  <Button variant="contained" onClick={doImport} disabled={importing || importRows.every((r) => r.problem)}>
                    {importing ? <CircularProgress size={22} /> : `Import ${importRows.filter((r) => !r.problem).length}`}
                  </Button>
                </>
              )}
            </TabPanel>

            {/* ---------------------------------------------------------- Settings */}
            <TabPanel value={tab} index={3}>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Details</Typography>
                  <Stack spacing={2}>
                    <TextField label="Group name" value={settingsForm.name} onChange={(e) => setSettingsForm({ ...settingsForm, name: e.target.value })} fullWidth />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        label="Budget" type="number" value={settingsForm.budget} fullWidth
                        onChange={(e) => setSettingsForm({ ...settingsForm, budget: e.target.value })}
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      />
                      <TextField
                        label="Exchange date" type="date" value={settingsForm.exchangeDate} fullWidth InputLabelProps={{ shrink: true }}
                        onChange={(e) => setSettingsForm({ ...settingsForm, exchangeDate: e.target.value })}
                      />
                    </Stack>
                    <TextField label="Where" value={settingsForm.exchangeLocation} fullWidth placeholder="e.g. Grandma's house, or a video call link" onChange={(e) => setSettingsForm({ ...settingsForm, exchangeLocation: e.target.value })} />
                    <Autocomplete
                      multiple freeSolo options={[]} value={settingsForm.coOrganizerEmails}
                      onChange={(_, v) => setSettingsForm({ ...settingsForm, coOrganizerEmails: v.map((x) => String(x).trim().toLowerCase()).filter(isValidEmail) })}
                      renderTags={(value, getTagProps) => value.map((o, i) => <Chip size="small" label={o} {...getTagProps({ index: i })} />)}
                      renderInput={(params) => <TextField {...params} label="Co-organizers" helperText="Type an email and press Enter. Co-organizers can do everything except delete the group." />}
                    />
                  </Stack>
                </Box>

                <Divider />
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Matching</Typography>
                  <FormControlLabel
                    control={<Switch checked={settingsForm.autoExcludePreviousYears} onChange={(e) => setSettingsForm({ ...settingsForm, autoExcludePreviousYears: e.target.checked })} />}
                    label="Don't let anyone draw the same person as last year"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6 }}>
                    Works when this group was created with "Create next year's group" from last year's.
                  </Typography>
                  <FormControlLabel
                    control={<Switch checked={settingsForm.surveyEnabled} onChange={(e) => setSettingsForm({ ...settingsForm, surveyEnabled: e.target.checked })} />}
                    label="Gift preference survey"
                  />
                </Box>

                <Divider />
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Reminders</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Sent by email, and by text to anyone who verified a phone number in their settings. Texts never include names.
                  </Typography>
                  <FormControlLabel
                    control={<Switch checked={settingsForm.reminders.matchReady} onChange={(e) => setSettingsForm({ ...settingsForm, reminders: { ...settingsForm.reminders, matchReady: e.target.checked } })} />}
                    label="Notify everyone when matches are drawn"
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={<Switch checked={settingsForm.reminders.addIdeasAfterDays != null} onChange={(e) => setSettingsForm({ ...settingsForm, reminders: { ...settingsForm.reminders, addIdeasAfterDays: e.target.checked ? 7 : null } })} />}
                      label="Nudge people to add gift ideas"
                    />
                    {settingsForm.reminders.addIdeasAfterDays != null && (
                      <TextField
                        size="small" type="number" label="days after draw" sx={{ width: 140 }}
                        value={settingsForm.reminders.addIdeasAfterDays}
                        onChange={(e) => setSettingsForm({ ...settingsForm, reminders: { ...settingsForm.reminders, addIdeasAfterDays: Math.max(1, parseInt(e.target.value) || 1) } })}
                      />
                    )}
                  </Box>
                  <FormControlLabel
                    control={<Switch checked={settingsForm.reminders.weeklyNewIdeas} onChange={(e) => setSettingsForm({ ...settingsForm, reminders: { ...settingsForm.reminders, weeklyNewIdeas: e.target.checked } })} />}
                    label="Weekly note when new ideas are added for someone's person"
                  />
                </Box>

                <Button variant="contained" onClick={saveSettings} disabled={savingSettings} sx={{ alignSelf: 'flex-start' }}>
                  {savingSettings ? <CircularProgress size={22} /> : 'Save settings'}
                </Button>

                <Divider />
                <Box>
                  <Typography variant="subtitle1" color="error" sx={{ mb: 1 }}>Danger zone</Typography>
                  <Button variant="outlined" color="error" startIcon={<Delete />} onClick={() => setDeleteOpen(true)}>Delete group</Button>
                </Box>
              </Stack>
            </TabPanel>
          </CardContent>
        </Card>
      )}

      {/* Add participant */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add a person</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField autoFocus label="Name" fullWidth value={newP.name} onChange={(e) => setNewP({ ...newP, name: e.target.value })} />
            <TextField label="Email" type="email" fullWidth value={newP.email} onChange={(e) => setNewP({ ...newP, email: e.target.value })} helperText="They'll sign in with this email." />
            <Autocomplete
              freeSolo options={households} value={newP.householdId}
              onInputChange={(_, v) => setNewP({ ...newP, householdId: v })}
              renderInput={(params) => <TextField {...params} label="Household (optional)" helperText="People in the same household won't draw each other." />}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button onClick={addParticipant} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm regenerate */}
      <Dialog open={confirmRegen} onClose={() => setConfirmRegen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Redraw matches?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>This replaces everyone's current person and notifies the whole group again.</Alert>
          <Typography>Anyone who already bought a gift will have a different person. Only do this if someone joined or left.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRegen(false)}>Keep current matches</Button>
          <Button variant="contained" color="warning" onClick={() => draw(false, true)} disabled={drawing}>Redraw and notify</Button>
        </DialogActions>
      </Dialog>

      {/* Delete group */}
      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete group?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>This cannot be undone.</Alert>
          <Typography>
            Delete <strong>{group?.name}</strong> with all participants, matches, gift ideas and messages?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <Button onClick={deleteGroup} variant="contained" color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={22} /> : 'Delete group'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} message={toast} />
    </Layout>
  );
};

export default GroupManager;
